import { App, Plugin, Ref, ref } from "vue";
import {
  AllgemeinApi,
  AllgemeinApiInterface,
  AorvApi,
  AorvApiInterface,
  Configuration as BackendConfiguration,
  ConfigurationParameters,
  PrognoseApi,
  PrognoseApiInterface,
  RailiumApi,
  RailiumApiInterface,
  BerichtsmanagementApi,
  BerichtsmanagementApiInterface,
  RsrImportApi,
  RsrImportApiInterface,
  RvcApi,
  RvcApiInterface,
  StatistikApi,
  StatistikApiInterface,
  StrategieApi,
  StrategieApiInterface,
  SystemApi,
  SystemApiInterface,
} from "@apis/backend";
import { AccessToken, isValidAccessToken } from "./apiHelpers";
import { ApiMiddleware } from "./ApiMiddleware";
import {
  BaseApis,
  setupCallWhenTokenInitialized,
} from "@/plugins/baseApiPlugin";

export interface BackendApis extends BaseApis {
  system: SystemApiInterface;
  allgemein: AllgemeinApiInterface;
  railium: RailiumApiInterface;
  berichtsmanagement: BerichtsmanagementApiInterface;
  prognose: PrognoseApiInterface;
  strategie: StrategieApiInterface;
  statistik: StatistikApiInterface;
  rvc: RvcApiInterface;
  rsr: RsrImportApiInterface;
  aorv: AorvApiInterface;
  callWhenTokenInitialized: (toCall: () => void | (() => void)[]) => void;
}

export const apiPlugin: Plugin = {
  install: (app: App) => {
    app.config.globalProperties.$backendApiAccessToken = ref<AccessToken>();
    app.config.globalProperties.$backendApiAccessTokenLock = ref(false);

    const accessTokenToHeader: (at?: AccessToken) => string = (at) => {
      if (isValidAccessToken(at)) {
        return "Bearer " + at!.accessToken;
      } else {
        throw new Error("BackendApi: Missing access token");
      }
    };

    const configBase: ConfigurationParameters = {
      middleware: [new ApiMiddleware(app.config.globalProperties.$language)],
    };
    const backendConfig = new BackendConfiguration({
      ...configBase,
      basePath: "/api/backend",
      accessToken: () =>
        accessTokenToHeader(
          app.config.globalProperties.$backendApiAccessToken.value,
        ),
    });

    const isTokenInitialized = ref(false);

    app.config.globalProperties.$backendApis = {
      system: new SystemApi(backendConfig),
      allgemein: new AllgemeinApi(backendConfig),
      railium: new RailiumApi(backendConfig),
      berichtsmanagement: new BerichtsmanagementApi(backendConfig),
      prognose: new PrognoseApi(backendConfig),
      strategie:         new StrategieApi(backendConfig),
      statistik: new StatistikApi(backendConfig),
      rvc: new RvcApi(backendConfig),
      rsr:  new RsrImportApi(backendConfig),
      aorv: new AorvApi(backendConfig),
      isTokenInitialized: isTokenInitialized,
      isTokenCurrentlyValid: ref(false),
      callWhenTokenInitialized:
        setupCallWhenTokenInitialized(isTokenInitialized),
    };
  },
};

declare module "vue" {
  interface ComponentCustomProperties {
    $backendApiAccessToken: Ref<AccessToken | undefined>;
    $backendApis: BackendApis;
    $backendApiAccessTokenLock: Ref<boolean>;
  }
}
